
import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Check, ArrowRight, Quote, Mail, AlertTriangle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.png";
import { plans as sharedPlans } from "@/config/plans";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { X } from "lucide-react";
import { ensureOrgSetup, getPendingOrgName, setPendingOrgName } from "@/lib/orgSetup";
import { getOrgForUser } from "@/lib/orgBilling";
import { trackFreeRegistration } from "@/lib/tracking/conversion";

export default function AdminLogin() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Mode derived from URL, defaulting to login
  const mode = (searchParams.get("mode") as "login" | "signup") || "login";
  const billingStatus = searchParams.get("billing"); // success | cancel

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [plan, setPlan] = useState("starter"); // Default to starter
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showFinishSetup, setShowFinishSetup] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [signupCooldownUntil, setSignupCooldownUntil] = useState<number | null>(null);
  const [signupCooldownSeconds, setSignupCooldownSeconds] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const setMode = (newMode: "login" | "signup") => {
    setSearchParams({ mode: newMode });
    setStep(1);
    setShowFinishSetup(false);
  };

  // Persist wizard state to localStorage for post-redirect restoration
  const persistSignupState = () => {
    localStorage.setItem("signup_state", JSON.stringify({
      email,
      companyName,
      plan,
      step: 3 // Restoration point: Post-Billing Success
    }));
  };

  const clearSignupState = () => {
    localStorage.removeItem("signup_state");
  };

  const restoreSignupState = () => {
    const saved = localStorage.getItem("signup_state");
    if (saved) {
      try {
        const { email, companyName, plan, step } = JSON.parse(saved);
        if (email) setEmail(email);
        if (companyName) setCompanyName(companyName);
        if (plan) setPlan(plan);
        if (step) setStep(step);
      } catch (e) {
        console.error("Failed to restore signup state", e);
      }
    }
  };

  const finishSetup = async () => {
    if (!companyName || companyName.trim().length < 2) {
      toast({ title: "Please enter a valid company name", variant: "destructive" });
      return;
    }
    setIsLoading(true);
    setLoadingMessage("Setting up organization...");
    try {
      const { error } = await supabase.functions.invoke('create-organization', {
        body: { name: companyName }
      });
      if (error) throw error;

      trackFreeRegistration();
      toast({ title: "Organization setup complete!" });
      localStorage.removeItem("signup_state");
      navigate("/admin/dashboard");
    } catch (err: unknown) {
      console.error("Setup failed:", err);
      toast({ title: "Setup failed", description: "Please try again or contact support.", variant: "destructive" });
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  // Check auth & org status on mount
  useEffect(() => {
    let mounted = true;

    const runAuthCheck = async () => {
      const { data: { session } } = await supabase.auth.getSession();

      // Handle post-payment redirect flow
      if (billingStatus === 'success') {
        if (session) {
          // Poll for org plan update
          let attempts = 0;
          let orgUpdated = false;
          while (attempts < 5 && !orgUpdated) {
            const org = await getOrgForUser(supabase);
            if (org && (org.plan_key === 'starter' || org.plan_key === 'growth')) {
              orgUpdated = true;
            } else {
              await new Promise(r => setTimeout(r, 1500));
              attempts++;
            }
          }

          toast({
            title: "Subscription Activated",
            description: "Payment confirmed. Taking you to your dashboard...",
          });

          // Clear billing param
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.delete('billing');
          window.history.replaceState({}, '', currentUrl.toString());

          clearSignupState();
          navigate("/admin/dashboard");
        } else {
          // No session but billing=success? Should likely login.
          setMode('login');
          setIsCheckingAuth(false);
        }
        return;
      } else if (billingStatus === 'cancel') {
        restoreSignupState();
        setMode("signup");
        setStep(2); // Back to plan selection
        toast({
          title: "Payment Cancelled",
          description: "You can choose a different plan or try again.",
          variant: "default"
        });

        // Clear billing param
        const newUrl = window.location.pathname + window.location.search.replace(/[\?&]billing=[^&]+/, '').replace(/^&/, '?');
        window.history.replaceState({}, '', newUrl);

        setIsCheckingAuth(false);
        return;
      }

      if (!session) {
        if (mounted) setIsCheckingAuth(false);
        return;
      }

      // Normal auth check
      const { success } = await ensureOrgSetup(supabase, session.user.id);

      if (mounted) {
        if (success) {
          if (!billingStatus && mode === 'login') {
            localStorage.removeItem("signup_state");
            navigate("/admin/dashboard");
          } else {
            setIsCheckingAuth(false);
          }
        } else {
          // Needs setup
          const pendingName = getPendingOrgName() || session.user.user_metadata?.company_name;
          if (pendingName) setCompanyName(pendingName);
          setShowFinishSetup(true);
          setIsCheckingAuth(false);
        }
      }
    };

    runAuthCheck();
  }, [navigate, billingStatus, mode]);

  // Cooldown Timer Effect
  useEffect(() => {
    if (!signupCooldownUntil) return;

    const interval = setInterval(() => {
      const now = Date.now();
      if (now >= signupCooldownUntil) {
        setSignupCooldownUntil(null);
        setSignupCooldownSeconds(0);
        clearInterval(interval);
      } else {
        setSignupCooldownSeconds(Math.ceil((signupCooldownUntil - now) / 1000));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [signupCooldownUntil]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setLoadingMessage("Logging in...");

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      if (data.session) {
        const { success } = await ensureOrgSetup(supabase, data.session.user.id);
        if (success) {
          localStorage.removeItem("signup_state");
          navigate("/admin/dashboard");
          return;
        } else {
          const pendingName = getPendingOrgName() || data.session.user.user_metadata?.company_name;
          if (pendingName) setCompanyName(pendingName);
          setShowFinishSetup(true);
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "An error occurred";
      toast({ title: "Login Failed", description: message, variant: "destructive" });
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  const validatePassword = (pwd: string) => {
    return {
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /[0-9]/.test(pwd),
    };
  };

  const validateEmail = (val: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!val || !emailRegex.test(val)) {
      setEmailError("Please enter a valid email address.");
      return false;
    }
    setEmailError("");
    return true;
  };

  const passwordCriteria = validatePassword(password);
  const isPasswordValid = Object.values(passwordCriteria).every(Boolean);

  const handleSignupNext = () => {
    if (step === 1) {
      if (!email || !password || !confirmPassword || !companyName) {
        toast({ title: "Please fill in all fields", variant: "destructive" });
        return;
      }

      const isEmailValid = validateEmail(email);
      if (!isEmailValid) return;

      if (!isPasswordValid) {
        toast({ title: "Please meet all password requirements", variant: "destructive" });
        return;
      }

      if (password !== confirmPassword) {
        toast({ title: "Passwords do not match", variant: "destructive" });
        return;
      }
      setStep(2);
    }
  };

  const handleConsolidatedSignup = async () => {
    if (isLoading || (signupCooldownUntil && Date.now() < signupCooldownUntil)) return;

    setIsLoading(true);
    setLoadingMessage("Creating account...");

    try {
      // 1. Create Supabase Auth User
      // Save state first in case we redirect
      persistSignupState();
      setPendingOrgName(companyName);

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            company_name: companyName,
            plan: plan
          },
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) throw error;

      if (data.session) {
        // 2. Setup Organization immediately
        setLoadingMessage("Setting up workspace...");
        const { error: orgError } = await supabase.functions.invoke('create-organization', {
          body: { name: companyName }
        }); // Idempotent

        if (orgError) {
          console.error("Org setup warning:", orgError);
        }

        // 3. Handle Plan Logic
        if (plan === 'free') {
          // Free Plan: Directly to Dashboard
          clearSignupState();
          trackFreeRegistration();
          toast({ title: "Welcome to RaffleKit!", description: "Your account is ready." });
          navigate("/admin/dashboard");
          return;
        } else {
          // Paid Plan: Redirect to Stripe
          setLoadingMessage("Redirecting to checkout...");
          const returnUrl = `${window.location.origin}/admin?billing=success`;

          const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('billing-checkout', {
            body: { planKey: plan, successUrl: returnUrl, cancelUrl: `${window.location.origin}/admin?billing=cancel` }
          });

          if (checkoutError || !checkoutData?.url) {
            throw new Error(checkoutError?.message || "Failed to initialize checkout");
          }

          window.location.assign(checkoutData.url);
        }

      } else {
        // Email confirmation required (No Session)
        // Show Step 4 (Email Check)
        setStep(4);
      }

    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "An error occurred";

      // Rate Limit Handling
      if (
        (typeof err === 'object' && err !== null && 'status' in err && (err as any).status === 429) ||
        message.toLowerCase().includes("rate limit")
      ) {
        const cooldownMs = 60000; // 60 seconds
        setSignupCooldownUntil(Date.now() + cooldownMs);
        setSignupCooldownSeconds(60);
        toast({
          title: "Too many attempts",
          description: "Please wait a moment before trying again.",
          variant: "destructive"
        });
      } else {
        toast({ title: "Signup Failed", description: message, variant: "destructive" });
      }
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  const handlePostBillingContinue = () => {
    clearSignupState();
    navigate("/admin/dashboard");
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Finish Setup UI
  if (showFinishSetup) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white font-sans p-6">
        <div className="w-full max-w-md mx-auto text-center animate-in fade-in zoom-in duration-300">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
          <h2 className="font-heading text-3xl font-bold text-gray-900 mb-2">Finishing Setup</h2>
          <p className="text-gray-500 mb-8">
            We just need to set up your organization workspace.
          </p>

          <div className="space-y-4 text-left bg-gray-50 p-6 rounded-xl border border-gray-100">
            <div className="space-y-2">
              <Label htmlFor="setupCompanyName" className="text-base">Company Name</Label>
              <Input
                id="setupCompanyName"
                placeholder="Acme Inc."
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className="h-12 text-base"
              />
            </div>
            <Button onClick={finishSetup} className="w-full h-12 text-lg font-bold" variant="accent" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  {loadingMessage || "Setting up..."}
                </>
              ) : "Complete Setup"}
            </Button>
          </div>
          <div className="mt-6">
            <button onClick={() => supabase.auth.signOut()} className="text-sm text-gray-400 hover:text-gray-600 underline">
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen md:h-screen flex flex-col md:flex-row bg-white font-sans md:overflow-hidden">
      {/* Left Panel - Brand / Visual */}
      <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-[#1EAFA0] to-[#F97116] p-8 lg:p-12 text-white flex-col justify-between relative h-full overflow-hidden">
        {/* Abstract shapes/overlay */}
        <div className="absolute top-0 left-0 w-full h-full bg-black/10 z-0 pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-white/20 rounded-full blur-3xl z-0" />

        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-2 mb-12 lg:mb-0 hover:opacity-90 transition-opacity w-fit">
            <img src={logo} alt="RaffleKit" className="w-10 h-10 object-contain brightness-0 invert" />
            <span className="font-heading font-bold text-2xl tracking-tight text-white">RaffleKit</span>
          </Link>

          <div className="hidden lg:block mt-24">
            <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-tight mb-6">
              Grow your audience with simple, powerful giveaways.
            </h1>
            <p className="text-xl text-white/90 max-w-lg">
              Join thousands of small businesses running viral contests in minutes.
            </p>
          </div>
        </div>

        <div className="relative z-10 hidden lg:block">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <div className="flex gap-1 text-yellow-300 mb-3">
              {[1, 2, 3, 4, 5].map(i => <div key={i}>★</div>)}
            </div>
            <p className="text-lg font-medium mb-4">"RaffleKit made it incredibly easy to launch our first giveaway. We doubled our email list in just one week!"</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold">S</div>
              <div>
                <div className="font-bold">Sarah Jenkins</div>
                <div className="text-sm text-white/70">Founder, Bloom & Grow</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full md:w-1/2 flex flex-col items-center justify-center p-6 md:p-12 md:h-full md:overflow-y-auto bg-white">
        <div className="w-full max-w-md mx-auto">
          <div className="md:hidden w-full mb-8 flex justify-center">
            <Link to="/" className="flex items-center gap-2">
              <img src={logo} alt="RaffleKit" className="w-8 h-8 object-contain" />
              <span className="font-heading font-bold text-xl tracking-tight text-gray-900">RaffleKit</span>
            </Link>
          </div>
          {mode === "login" ? (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="mb-8">
                <h2 className="font-heading text-3xl font-bold text-gray-900 mb-2">Welcome back</h2>
                <div className="flex items-center gap-2 text-gray-500">
                  <p>Don't have an account?</p>
                  <button onClick={() => setMode("signup")} className="text-primary font-bold hover:underline transition-colors">Create one</button>
                </div>
              </div>

              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-base">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12 text-base"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-base">Password</Label>
                    <Link to="#" className="text-sm font-medium text-gray-500 hover:text-primary transition-colors">
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 text-base"
                  />
                </div>

                <Button type="submit" className="w-full h-12 text-lg font-bold rounded-lg mt-2" disabled={isLoading} variant="accent">
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {loadingMessage || "Log in"}
                    </>
                  ) : (
                    "Log in"
                  )}
                </Button>
              </form>
            </div>
          ) : (
            <div className="w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
              {step === 1 && (
                <div>
                  <div className="mb-8">
                    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-2">Create your account</h2>
                    <div className="flex items-center gap-2 text-gray-500">
                      <p>Already have an account?</p>
                      <button onClick={() => setMode("login")} className="text-primary font-bold hover:underline transition-colors">Log in</button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Company Name */}
                    <div className="space-y-2">
                      <Label htmlFor="companyName" className="text-base">Company Name</Label>
                      <Input
                        id="companyName"
                        placeholder="Acme Inc."
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="h-12 text-base"
                      />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-base">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (emailError) validateEmail(e.target.value);
                        }}
                        onBlur={() => validateEmail(email)}
                        className={cn(
                          "h-12 text-base",
                          emailError ? "border-red-300 focus-visible:ring-red-200" : ""
                        )}
                      />
                      {emailError && (
                        <p className="text-sm text-red-500 flex items-center gap-1 animate-in fade-in slide-in-from-top-1 mt-1">
                          <X className="w-3 h-3" /> {emailError}
                        </p>
                      )}
                    </div>

                    {/* Password */}
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-base">Password</Label>
                        <Input
                          id="password"
                          type="password"
                          placeholder="••••••••"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className={cn(
                            "h-12 text-base",
                            password && !isPasswordValid ? "border-red-300 focus-visible:ring-red-200" : ""
                          )}
                        />
                      </div>

                      {/* Password Criteria Helper */}
                      <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                        <div className={cn("flex items-center gap-1.5 transition-colors", passwordCriteria.length ? "text-green-600" : "")}>
                          <div className="relative w-3 h-3 flex-shrink-0">
                            <Check
                              className="w-3 h-3 absolute inset-0 transition-opacity duration-150"
                              style={{ opacity: passwordCriteria.length ? 1 : 0 }}
                            />
                            <div
                              className="w-3 h-3 rounded-full border border-gray-300 absolute inset-0 transition-opacity duration-150"
                              style={{ opacity: passwordCriteria.length ? 0 : 1 }}
                            />
                          </div>
                          8+ characters
                        </div>
                        <div className={cn("flex items-center gap-1.5 transition-colors", passwordCriteria.uppercase ? "text-green-600" : "")}>
                          <div className="relative w-3 h-3 flex-shrink-0">
                            <Check
                              className="w-3 h-3 absolute inset-0 transition-opacity duration-150"
                              style={{ opacity: passwordCriteria.uppercase ? 1 : 0 }}
                            />
                            <div
                              className="w-3 h-3 rounded-full border border-gray-300 absolute inset-0 transition-opacity duration-150"
                              style={{ opacity: passwordCriteria.uppercase ? 0 : 1 }}
                            />
                          </div>
                          Uppercase letter
                        </div>
                        <div className={cn("flex items-center gap-1.5 transition-colors", passwordCriteria.lowercase ? "text-green-600" : "")}>
                          <div className="relative w-3 h-3 flex-shrink-0">
                            <Check
                              className="w-3 h-3 absolute inset-0 transition-opacity duration-150"
                              style={{ opacity: passwordCriteria.lowercase ? 1 : 0 }}
                            />
                            <div
                              className="w-3 h-3 rounded-full border border-gray-300 absolute inset-0 transition-opacity duration-150"
                              style={{ opacity: passwordCriteria.lowercase ? 0 : 1 }}
                            />
                          </div>
                          Lowercase letter
                        </div>
                        <div className={cn("flex items-center gap-1.5 transition-colors", passwordCriteria.number ? "text-green-600" : "")}>
                          <div className="relative w-3 h-3 flex-shrink-0">
                            <Check
                              className="w-3 h-3 absolute inset-0 transition-opacity duration-150"
                              style={{ opacity: passwordCriteria.number ? 1 : 0 }}
                            />
                            <div
                              className="w-3 h-3 rounded-full border border-gray-300 absolute inset-0 transition-opacity duration-150"
                              style={{ opacity: passwordCriteria.number ? 0 : 1 }}
                            />
                          </div>
                          Number
                        </div>
                      </div>
                    </div>

                    {/* Confirm Password */}
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-base">Confirm Password</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={cn(
                          "h-12 text-base",
                          confirmPassword && password !== confirmPassword ? "border-red-300 focus-visible:ring-red-200" : ""
                        )}
                      />
                      {confirmPassword && password !== confirmPassword && (
                        <p className="text-sm text-red-500 flex items-center gap-1 animate-in fade-in slide-in-from-top-1">
                          <X className="w-3 h-3" /> Passwords don't match
                        </p>
                      )}
                    </div>

                    <Button onClick={handleSignupNext} className="w-full h-12 text-lg font-bold mt-4" variant="accent">
                      Choose Plan <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div>
                  <div className="mb-8 text-center">
                    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-2">Select a plan</h2>
                    <p className="text-gray-500">You can always change this later.</p>
                  </div>

                  <div className="grid gap-4 mb-8">
                    {sharedPlans.map((p) => (
                      <div
                        key={p.id}
                        onClick={() => setPlan(p.id)}
                        className={cn(
                          "relative p-5 pl-12 rounded-xl border-2 transition-all cursor-pointer hover:bg-gray-50 flex flex-col",
                          plan === p.id ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-gray-200"
                        )}
                      >
                        {/* Left Radio Indicator */}
                        <div className={cn(
                          "absolute top-6 left-4 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors",
                          plan === p.id ? "border-primary" : "border-gray-300"
                        )}>
                          {plan === p.id && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
                        </div>

                        {/* Plan Header */}
                        <div className={cn(
                          "flex justify-between items-start w-full",
                          p.badge ? "mb-6" : "mb-2"
                        )}>
                          <div className="flex flex-col justify-center">
                            <span className="font-bold text-xl text-gray-900">{p.name}</span>
                          </div>
                          <div className="flex flex-col items-end relative">
                            <div className="flex items-baseline gap-1">
                              <span className="font-bold text-2xl text-gray-900">{p.price}</span>
                              <span className="text-xs text-gray-500">{p.description}</span>
                            </div>
                            {p.badge && (
                              <span className="absolute right-0 top-full mt-1 bg-[#F97116] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm text-center whitespace-nowrap z-10">
                                {p.badge}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Highlight Features */}
                        <div className="text-sm text-gray-600 mb-3 space-y-1">
                          {p.features.slice(0, 2).map((h, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                              <span>{h}</span>
                            </div>
                          ))}
                        </div>

                        {/* Accordion for Details */}
                        <Accordion type="single" collapsible className="w-full mt-auto">
                          <AccordionItem value="details" className="border-b-0">
                            <AccordionTrigger className="py-0 text-xs text-gray-500 hover:text-primary hover:no-underline justify-start gap-2">
                              See all features
                            </AccordionTrigger>
                            <AccordionContent className="pt-3 pb-0">
                              <ul className="space-y-1">
                                {p.features.slice(2).map((feature, i) => (
                                  <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                    <span>{feature}</span>
                                  </li>
                                ))}
                              </ul>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-3 flex-col">
                    {signupCooldownUntil && (
                      <Alert variant="destructive" className="mb-2">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>Rate Limit Exceeded</AlertTitle>
                        <AlertDescription>
                          Too many signup attempts. Try again in {signupCooldownSeconds}s.
                        </AlertDescription>
                      </Alert>
                    )}
                    <div className="flex gap-3">
                      <Button variant="outline" onClick={() => setStep(1)} className="h-12 px-6" disabled={isLoading}>Back</Button>
                      <Button onClick={handleConsolidatedSignup} className="h-12 flex-1 font-bold" variant="accent" disabled={isLoading || !!signupCooldownUntil}>
                        {isLoading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            {loadingMessage}
                          </>
                        ) : (
                          `Continue with ${sharedPlans.find(p => p.id === plan)?.name}`
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="font-heading text-3xl font-bold text-gray-900 mb-2">You're all set!</h2>
                  <p className="text-gray-500 mb-8 max-w-sm mx-auto">
                    Your subscription is active. Welcome aboard!
                  </p>

                  <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left border border-gray-100 shadow-sm">
                    <div className="grid grid-cols-2 gap-y-4 text-sm">
                      <div className="text-gray-500">Company</div>
                      <div className="font-medium text-right">{companyName}</div>

                      <div className="text-gray-500">Email</div>
                      <div className="font-medium text-right truncate pl-4">{email}</div>

                      <div className="text-gray-500">Plan</div>
                      <div className="font-bold text-primary text-right uppercase">{plan}</div>
                    </div>
                  </div>

                  <Button onClick={handlePostBillingContinue} className="w-full h-12 flex-1 font-bold" variant="accent">
                    Go to Dashboard
                  </Button>
                </div>
              )}

              {step === 4 && (
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Mail className="w-8 h-8 text-blue-600" />
                  </div>
                  <h2 className="font-heading text-3xl font-bold text-gray-900 mb-2">Check your email</h2>
                  <p className="text-gray-500 mb-8 max-w-sm mx-auto">
                    We've sent a confirmation link to <strong>{email}</strong>. Please verify your email to continue.
                  </p>

                  <Button variant="outline" onClick={() => setMode('login')} className="w-full h-12 font-bold">
                    Go to Login
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
